import { useEffect, useRef } from "react";

/*
  Exported because a caller sometimes has to wait for the box to stop moving
  before doing anything positional — PartnerSharing scrolls its newly revealed
  rows into view, and scrolling at a target that is still growing lands short.
*/
export const SMOOTH_HEIGHT_MS = 250;
const DURATION = SMOOTH_HEIGHT_MS;

/*
  Animates a content-sized box when its contents change size.

  The modal is `height: auto` — it is as tall as whatever is inside it — so
  opening a section within it makes it jump to the new size in one frame.

  CSS alone cannot smooth this out. `interpolate-size: allow-keywords` is the
  obvious candidate and it is the wrong tool: it animates `auto` to or from a
  length, and here the declared height is `auto` on both sides of the change.
  Nothing about the height DECLARATION changes, so no transition is triggered;
  only the used value moves. Tried, measured, removed.

  So the old height is captured and replayed. By the time a ResizeObserver
  fires, the box has already reflowed — the new size is the one on screen — so
  we set the height back to what it was, flush that, and then let it transition
  to where it had already got to.

  The observer watches the CONTENT, never the box, because the box's height is
  the thing being written to; observing it would feed back on itself.
*/
export const useSmoothHeight = (boxRef, contentRef) => {
  const settleRef = useRef(null);

  useEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    if (!box || !content || typeof ResizeObserver === "undefined") return undefined;
    // Respect the OS setting; this is decoration.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      return undefined;
    }

    let previous = box.getBoundingClientRect().height;

    const clear = () => {
      box.style.transition = "";
      box.style.height = "";
    };

    const observer = new ResizeObserver(() => {
      const next = box.getBoundingClientRect().height;
      // Sub-pixel noise, and the resize that follows our own cleanup.
      if (Math.abs(next - previous) < 2) {
        previous = next;
        return;
      }
      const from = previous;
      previous = next;

      box.style.transition = "none";
      box.style.height = `${from}px`;
      // Read forces the browser to accept that height before the next write,
      // which is what makes the write after it animate rather than snap.
      void box.offsetHeight;
      box.style.transition = `height ${DURATION}ms ease`;
      box.style.height = `${next}px`;

      clearTimeout(settleRef.current);
      settleRef.current = setTimeout(clear, DURATION + 30);
    });

    observer.observe(content);
    return () => {
      observer.disconnect();
      clearTimeout(settleRef.current);
      clear();
    };
  }, [boxRef, contentRef]);
};

export default useSmoothHeight;
